/**
 * Data layer for `igaming-negative-carryover-toggle`. Thin typed fetch wrappers over /api/v1/igaming-negative-carryover-toggle.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingNegativeCarryoverToggleRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingNegativeCarryoverToggleResponse {
	count: number;
	items: IgamingNegativeCarryoverToggleRow[];
}

export interface CreateIgamingNegativeCarryoverToggleBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-negative-carryover-toggle";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingNegativeCarryoverToggleResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingNegativeCarryoverToggleResponse>(res);
}

export async function create(body: CreateIgamingNegativeCarryoverToggleBody): Promise<IgamingNegativeCarryoverToggleRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingNegativeCarryoverToggleRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingNegativeCarryoverToggleQueryKeys = {
	all: ["igaming-negative-carryover-toggle"] as const,
	list: () => [...igamingNegativeCarryoverToggleQueryKeys.all] as const,
	detail: (id: string) => [...igamingNegativeCarryoverToggleQueryKeys.all, id] as const,
};
